import { z } from "zod"

import { entityIdSchema, legalEntityIdSchema } from "./identifiers.js"
import { invoiceDraftCreateV1Schema } from "./invoice-v1.js"

const timestamp = z.string().datetime({ offset: false })

export const billingTriggerOutcomeV1Schema = z.enum([
  "non_invoiceable",
  "review_required",
  "invoice_ready",
  "unbilled_work",
])

export const billingModeV1Schema = z.enum([
  "per_visit",
  "periodic_consolidated",
  "project_completion",
  "deposit_milestone",
  "manual_review",
  "non_invoiceable",
])

export const billingTriggerRecordV1Schema = z.object({
  ledgerEntryId: entityIdSchema,
  billingTriggerId: entityIdSchema,
  serviceOrderId: entityIdSchema,
  visitOrMilestoneId: entityIdSchema,
  outcome: billingTriggerOutcomeV1Schema,
  reasonCode: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
  occurredAt: timestamp,
}).strict()

export const billingTriggerReverseV1Schema = z.object({
  reversalEntryId: entityIdSchema,
  originalEntryId: entityIdSchema,
  reasonCode: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
  occurredAt: timestamp,
}).strict()

export const completionBillingTriggerV1Schema = z.object({
  ledgerEntryId: entityIdSchema,
  billingTriggerId: entityIdSchema,
  completionId: entityIdSchema,
  completionVersion: z.number().int().positive(),
  serviceOrderId: entityIdSchema,
  visitId: entityIdSchema,
  legalEntityId: entityIdSchema,
  branchId: entityIdSchema,
  costCenter: z.string().trim().min(1).max(140),
  billingMode: z.string().trim().min(1).max(100),
  outcome: billingTriggerOutcomeV1Schema,
  reasonCode: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
  allocatedAmountMinor: z.number().int().nonnegative(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  serviceOrderState: z.enum(["in_progress", "completed", "billing_review"]),
  occurredAt: timestamp,
}).strict()

export const depositAllocationApplyV1Schema = z.object({
  allocationEntryId: entityIdSchema,
  invoiceId: entityIdSchema,
  depositId: entityIdSchema,
  occurredAt: timestamp,
}).strict()

export const depositAllocationReverseV1Schema = z.object({
  reversalEntryId: entityIdSchema,
  originalAllocationEntryId: entityIdSchema,
  reasonCode: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
  occurredAt: timestamp,
}).strict()

export const unbilledWorkQueryV1Schema = z.object({
  legalEntityId: legalEntityIdSchema,
  outcome: z.enum(["all", "unbilled_work", "review_required"]).default("all"),
  cursor: entityIdSchema.nullable(),
  limit: z.number().int().min(1).max(100),
}).strict()

export const unbilledWorkItemV1Schema = z.object({
  ledgerEntryId: entityIdSchema,
  billingTriggerId: entityIdSchema,
  serviceOrderId: entityIdSchema,
  visitOrMilestoneId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: entityIdSchema,
  costCenter: z.string().trim().min(1).max(140),
  billingProfileId: entityIdSchema,
  billingPartyKey: z.string().trim().min(1).max(140),
  purchaseOrderKey: z.string().trim().min(1).max(140).nullable(),
  termsDays: z.number().int().min(0).max(365),
  billingMode: billingModeV1Schema,
  outcome: z.enum(["unbilled_work", "review_required"]),
  reasonCode: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
  candidateAmountMinor: z.number().int().nonnegative(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  taxCompatibilityKey: z.string().trim().min(1).max(140).nullable(),
  consolidationKey: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
  groupingBlockers: z.array(
    z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
  ).max(20),
  occurredAt: timestamp,
  expectedInvoiceAt: timestamp.nullable(),
  ageSeconds: z.number().int().nonnegative(),
  duplicateProtected: z.literal(true),
}).strict()

export const unbilledWorkPageV1Schema = z.object({
  projectionVersion: z.literal(1),
  items: z.array(unbilledWorkItemV1Schema).max(100),
  nextCursor: entityIdSchema.nullable(),
  complete: z.boolean(),
}).strict()

export const billingCandidateReviewV1Schema = z.object({
  candidateGroupId: entityIdSchema,
  selectedLedgerEntryIds: z.array(entityIdSchema).min(1).max(500)
    .refine((items) => new Set(items).size === items.length),
  invoice: invoiceDraftCreateV1Schema,
  reviewedAt: z.string().datetime({ offset: false }),
}).strict()

export const billingCandidateBranchAllocationV1Schema = z.object({
  invoiceLineId: entityIdSchema,
  branchId: entityIdSchema,
  costCenter: z.string().trim().min(1).max(140),
  amountMinor: z.number().int().nonnegative(),
}).strict()

export const billingCandidateDepositAllocationV1Schema = z.object({
  depositId: entityIdSchema,
  estimateId: entityIdSchema,
  estimateRevisionId: entityIdSchema,
  availableMinor: z.number().int().positive(),
  plannedMinor: z.number().int().positive(),
  lineAllocations: z.array(billingCandidateBranchAllocationV1Schema).min(1).max(500),
}).strict()

export const billingCandidateReviewResultV1Schema = z.object({
  entityType: z.literal("billing_candidate_group"),
  entityId: entityIdSchema,
  state: z.enum(["ready", "review_required"]),
  version: z.literal(1),
  invoiceId: entityIdSchema.nullable(),
  legalEntityId: legalEntityIdSchema,
  consolidationKey: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
  candidateCount: z.number().int().positive().max(500),
  totalCandidateMinor: z.number().int().nonnegative(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  blockers: z.array(z.string().regex(/^[a-z][a-z0-9_]{2,63}$/)).max(32),
  branchAllocations: z.array(billingCandidateBranchAllocationV1Schema).min(1).max(500),
  depositAllocations: z.array(billingCandidateDepositAllocationV1Schema).max(500),
}).strict()

export type BillingTriggerRecordV1 = z.infer<typeof billingTriggerRecordV1Schema>
export type BillingModeV1 = z.infer<typeof billingModeV1Schema>
export type BillingTriggerReverseV1 = z.infer<typeof billingTriggerReverseV1Schema>
export type CompletionBillingTriggerV1 = z.infer<
  typeof completionBillingTriggerV1Schema
>
export type DepositAllocationApplyV1 = z.infer<typeof depositAllocationApplyV1Schema>
export type DepositAllocationReverseV1 = z.infer<typeof depositAllocationReverseV1Schema>
export type UnbilledWorkItemV1 = z.infer<typeof unbilledWorkItemV1Schema>
export type UnbilledWorkPageV1 = z.infer<typeof unbilledWorkPageV1Schema>
export type UnbilledWorkQueryV1 = z.infer<typeof unbilledWorkQueryV1Schema>
export type BillingCandidateReviewV1 = z.infer<typeof billingCandidateReviewV1Schema>
export type BillingCandidateReviewResultV1 = z.infer<
  typeof billingCandidateReviewResultV1Schema
>
